import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, Camera, User as UserIcon, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface OnboardingData {
  name: string;
}

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, loading, refreshUser } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState<OnboardingData>({
    name: ''
  });
  const [avatar, setAvatar] = useState<string>('');
  const [loadingAvatar, setLoadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Protect route
  if (!loading && !user) {
    navigate('/login');
    return null;
  }

  if (!loading && user?.onboarding_completed) {
    navigate('/app');
    return null;
  }

  const canProceed = () => {
    return data.name.trim().length > 0;
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result?.toString().split(',')[1];
        resolve(base64String || '');
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && user) {
      setLoadingAvatar(true);
      try {
        const base64Data = await fileToBase64(file);
        const res = await fetch('/api/profile/avatar-upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: file.name,
            fileType: file.type,
            base64Data
          })
        });
            
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || "Failed to upload photo");
          
        setAvatar(result.avatarUrl);
        toast({ title: "Foto subida", description: "Tu foto se ha guardado correctamente." });
      } catch (error: any) {
        console.error("Error updating avatar:", error);
        toast({ title: "Error", description: error.message || "No se pudo subir la foto.", variant: "destructive" });
      } finally {
        setLoadingAvatar(false);
      }
    }
  };

  const triggerAvatarUpload = () => {
    if (!loadingAvatar && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const finish = async () => {
    if (user) {
      try {
        const res = await fetch('/api/profile/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            payload: {
              full_name: data.name,
              onboarding_completed: true
            }
          })
        });
        
        const result = await res.json();
          
        if (!res.ok) throw new Error(result.error || "Failed to save profile");
        
        await refreshUser();
        
        localStorage.setItem('onboarding_completed', 'true');
        localStorage.setItem('user_name', data.name);
        navigate('/app');
      } catch (error: any) {
        console.error("Error saving user profile:", error);
        navigate('/app');
      }
    } else {
      navigate('/login');
    }
  };

  const renderStep = () => {
    return (
      <div className="space-y-8">
        <div className="flex flex-col items-center justify-center pt-4">
          <div 
            className="relative h-28 w-28 rounded-full bg-muted flex items-center justify-center border-2 border-dashed border-primary/30 cursor-pointer overflow-hidden group"
            onClick={triggerAvatarUpload}
          >
            {avatar ? (
              <img src={avatar} alt="Avatar" className="h-full w-full object-cover" />
            ) : (
              <div className="flex flex-col items-center text-muted-foreground group-hover:text-primary transition-colors">
                {loadingAvatar ? <Loader2 className="h-6 w-6 animate-spin" /> : <Camera className="h-6 w-6 mb-1" />}
                <span className="text-[10px] font-medium">Subir foto</span>
              </div>
            )}
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Camera className="h-6 w-6 text-white" />
            </div>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleAvatarChange} 
              accept="image/*" 
              className="hidden" 
            />
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Tu foto es opcional</p>
        </div>

        <div className="space-y-4">
          <div>
            <h2 className="text-2xl font-heading font-bold mb-2">¿Cómo podemos llamarte?</h2>
            <p className="text-sm text-muted-foreground">Tu nombre es necesario para personalizar tu plan.</p>
          </div>
          <Input
            placeholder="Tu nombre completo"
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            className="rounded-xl text-lg h-14"
            autoFocus
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="sticky top-0 bg-background/80 backdrop-blur-md z-10 px-4 pt-4 pb-2 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <span className="text-lg">🍃</span>
          <span className="text-sm font-medium">Configuración de perfil</span>
        </div>
        <div className="h-1 bg-muted rounded-full overflow-hidden max-w-[200px] mx-auto">
          <div className="h-full gradient-primary rounded-full transition-all duration-500" style={{ width: `100%` }} />
        </div>
      </div>

      <div className="flex-1 px-6 py-4 max-w-md mx-auto w-full animate-fade-in">
        {renderStep()}
      </div>

      <div className="sticky bottom-0 bg-background/80 backdrop-blur-md border-t border-border p-4">
        <div className="flex gap-3 max-w-md mx-auto">
          <Button
            size="lg"
            className="flex-1 h-14 rounded-xl text-lg font-semibold"
            disabled={!canProceed() || loadingAvatar}
            onClick={() => finish()}
          >
            {loadingAvatar ? 'Subiendo foto...' : (
              <>Comenzar mi jornada <Check className="h-5 w-5 ml-2" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;